import React, { useCallback, useEffect, useMemo } from 'react';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { ArrowRightIcon, ChecksIcon, XIcon } from '@phosphor-icons/react';

import { MPFIconButton } from 'volto-ministerio-publico-federal/components/UI';
import PainelItem from './PainelItem';
import StatusPill from './StatusPill';
import { useNotificacoes } from './useNotificacoes';
import type { Notificacao } from 'volto-ministerio-publico-federal/helpers/Notificacoes';

const ITENS_NO_PAINEL = 10;

type Props = {
  closeMenu: () => void;
  theToolbar?: React.RefObject<HTMLDivElement>;
};

const focarSino = () =>
  (
    document.getElementById('toolbar-notifications') as HTMLElement | null
  )?.focus();

/**
 * Painel do sino, aberto pela Toolbar (`toolbarComponents.notifications`),
 * ancorado embaixo como o menu pessoal. Diálogo não modal: Esc fecha e
 * devolve o foco ao sino. Mostra só as não lidas; o histórico completo e a
 * Minha fila ficam na página /notificacoes.
 */
const NotificationsPanel = ({ closeMenu }: Props) => {
  const { estado, carregar, carregarFila, marcarLida, marcarTodas } =
    useNotificacoes();
  useEffect(() => {
    carregar('nao_lidas', 0, ITENS_NO_PAINEL);
  }, [carregar]);

  // A fila decide quais itens ainda "requerem sua ação".
  useEffect(() => {
    carregarFila();
  }, [carregarFila]);

  const fechar = useCallback(() => {
    closeMenu();
    focarSino();
  }, [closeMenu]);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [fechar]);

  const abrir = (item: Notificacao) => {
    marcarLida(item, true);
    closeMenu();
  };

  const { lista, fila, contagem } = estado;
  const itens = lista.filtro === 'nao_lidas' ? lista.items : [];
  const uidsNaFila = useMemo(
    // Sugestões (publicação nacional opcional) não "requerem sua ação".
    () => new Set(fila.items.filter((i) => !i.opcional).map((i) => i.uid)),
    [fila.items],
  );
  return (
    <div
      id="mpf-notificacoes-painel"
      className="mpf-notificacoes mpf-notificacoes--painel pastanaga-menu"
      role="dialog"
      aria-modal="false"
      aria-labelledby="mpf-notificacoes-titulo"
    >
      <header className="mpf-notificacoes__painel-cabecalho">
        <h2
          id="mpf-notificacoes-titulo"
          className="mpf-notificacoes__painel-titulo-painel"
        >
          Notificações
        </h2>
        {contagem.nao_lidas > 0 && (
          <StatusPill tom="novas">
            {contagem.nao_lidas === 1
              ? '1 nova'
              : `${contagem.nao_lidas > 99 ? '99+' : contagem.nao_lidas} novas`}
          </StatusPill>
        )}
        <span className="mpf-notificacoes__painel-espaco" />
        <MPFIconButton
          icon={ChecksIcon}
          label="Marcar todas como lidas"
          variant="ghost"
          size="m"
          disabled={contagem.nao_lidas === 0}
          onClick={() => marcarTodas()}
        />
        <MPFIconButton
          icon={XIcon}
          label="Fechar notificações"
          variant="ghost"
          size="m"
          onClick={fechar}
        />
      </header>

      <div className="mpf-notificacoes__painel-abas">
        <h3 className="mpf-notificacoes__painel-aba mpf-notificacoes__painel-aba--ativa">
          Não lidas
          {contagem.nao_lidas > 0 && (
            <span className="mpf-notificacoes__painel-contador">
              {contagem.nao_lidas > 99 ? '99+' : contagem.nao_lidas}
            </span>
          )}
        </h3>
      </div>

      <div className="mpf-notificacoes__conteudo" aria-busy={lista.loading}>
        {lista.error ? (
          <p className="mpf-notificacoes__vazio" role="alert">
            Não foi possível carregar as notificações.
          </p>
        ) : lista.loading && itens.length === 0 ? (
          <p className="mpf-notificacoes__vazio">Carregando…</p>
        ) : itens.length === 0 ? (
          <p className="mpf-notificacoes__vazio">
            Tudo em dia: nenhuma notificação não lida.
          </p>
        ) : (
          <ul className="mpf-notificacoes__lista">
            {itens.map((item) => (
              <PainelItem
                key={item.id}
                item={item}
                naFila={uidsNaFila.has(item.uid)}
                onAbrir={abrir}
                onAlternarLida={(n) => marcarLida(n, !n.lida)}
              />
            ))}
          </ul>
        )}
      </div>

      <footer className="mpf-notificacoes__painel-rodape">
        <UniversalLink href="/notificacoes" onClick={closeMenu}>
          Ver todas as notificações
          <ArrowRightIcon size={16} weight="bold" aria-hidden="true" />
        </UniversalLink>
      </footer>
    </div>
  );
};

export default NotificationsPanel;
